import React, {useEffect, useState} from 'react';
import { Link } from 'react-router-dom';
import MediaQuery from 'react-responsive';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
// DS
import { VertCarousel, VertContainer } from '@vertem-core/vertice-ds';
import generateUUID from 'Utils/generateUUID';
// STYLES
import 'Assets/styles/default/components/Hero/HeroStyles.less';
import usePipeContext from 'Services/Providers/PipeProvider/usePipeContext';
import PipeProvider from 'Services/Providers/PipeProvider';
import { BannerType } from 'Constants/Constants';
import isExternalLink from 'Utils/isExternalLink';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import useGA from "Utils/Ga";
import CallMethodAusterity from "Utils/callMethodAusterity";

const CreateLink = ({ children, url, target = '_self', onClick }) => {
  if (isExternalLink(url)) {
    return (
      <a onClick={() => {
          if (onClick) onClick()
      }} href={url} target={target}>
        {children}
      </a>
    );
  } else {
    return (
      <Link
          to={url || '#'}
          target={target}
          onClick={() => {
              if (onClick) onClick()
          }}
      >
        {children}
      </Link>
    );
  }
};

const setGaEventHeroBanner = (banner, eventGaUnlimited) => {
    eventGaUnlimited("select_promotion", {
        items: [
            {
                creative_name: banner.name,
                creative_slot: `banner_${ banner.id }`,
                promotion_name: banner.title,
            }
        ]
    })
}

const DesktopBanners = ({ banners }) => {
    const { eventGaUnlimited } = useGA()

  if (banners.length > 0) {
    const filteredBanners = banners
      .map((obj) => {
        const { images } = obj;
        const filteredImages = images.filter(
          (image) => image.bannerType === BannerType.desktop
        );

        if (filteredImages.length > 0) {
          return {
            ...obj,
            images: filteredImages[0],
          };
        }
        return null;
      })
      .filter(Boolean);

    return (
      <VertCarousel
        className="banner-top-header"
        infinite
        autoplay={filteredBanners.length > 1}
        autoplaySpeed={7000}
      >
        {filteredBanners.map((banner, index) => (
          <div className="desktop-banners text-center" key={generateUUID()}>
            <CreateLink onClick={() => {
                setGaEventHeroBanner(banner, eventGaUnlimited)
            }} url={banner.redirectUrn} target={banner.target}>
              <LazyLoadImage
                style={{
                  maxWidth: 'fit-content',
                  objectFit: 'contain',
                }}
                alt={banner.name}
                className={`image lazy-image`}
                key={`showcase-image-index-${index}`}
                src={banner?.images?.link || ''}
                placeholder={
                  <span
                    style={{
                      height: '100%',
                      width: '100%',
                      backgroundColor: '#090',
                    }}
                  ></span>
                }
              />
            </CreateLink>
          </div>
        ))}
      </VertCarousel>
    );
  }

  return '';
};
const TabletBanners = ({ banners }) => {
    const { eventGaUnlimited } = useGA()
  if (banners.length > 0) {
    const filteredBanners = banners
      .map((obj) => {
        const { images } = obj;
        const filteredImages = images.filter(
          (image) => image.bannerType === BannerType.tablet
        );
        if (filteredImages.length > 0) {
          return {
            ...obj,
            images: filteredImages[0],
          };
        }
        return null;
      })
      .filter(Boolean);

    return (
      <VertCarousel
        className="banner-top-header"
        infinite
        autoplay={filteredBanners.length > 1}
        autoplaySpeed={7000}
      >
        {filteredBanners.map((banner, index) => (
          <div className="desktop-banners text-center" key={generateUUID()}>
            <CreateLink onClick={() => {
                setGaEventHeroBanner(banner, eventGaUnlimited)
            }} url={banner.redirectUrn} target={banner.target}>
              <LazyLoadImage
                style={{
                  maxWidth: 'fit-content',
                  objectFit: 'cover',
                }}
                alt={banner.name}
                className="image lazy-image"
                key={`showcase-image-index-${index}`}
                src={banner?.images?.link || ''}
                placeholder={
                  <span
                    style={{
                      height: '100%',
                      width: '100%',
                      backgroundColor: '#090',
                    }}
                  ></span>
                }
              />
            </CreateLink>
          </div>
        ))}
      </VertCarousel>
    );
  }

  return '';
};

const MobileBanners = ({ banners }) => {
    const { eventGaUnlimited } = useGA()
  if (banners.length > 0) {
    const filteredBanners = banners
      .map((obj) => {
        const { images } = obj;
        const filteredImages = images.filter(
          (image) => image.bannerType === BannerType.mobile
        );

        if (filteredImages.length > 0) {
          return {
            ...obj,
            images: filteredImages[0],
          };
        }
        return null;
      })
      .filter(Boolean);

    return (
      <div className="banner-mobile-container">
        <VertCarousel
          infinite
          autoplay={filteredBanners.length > 1}
          autoplaySpeed={7000}
        >
          {filteredBanners.map((banner, index) => (
            <div className="mobile-banners" key={generateUUID()}>
              <CreateLink onClick={() => {
                  setGaEventHeroBanner(banner, eventGaUnlimited)
              }} url={banner.redirectUrn} target={banner.target}>
                <LazyLoadImage
                  style={{
                    minHeight: '125px',
                    maxWidth: 'fit-content',
                    objectFit: 'cover',
                  }}
                  alt={banner.name}
                  className="image lazy-image"
                  key={`showcase-image-index-${index}`}
                  src={banner?.images?.link || ''}
                  placeholder={
                    <span
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundColor: '#090',
                      }}
                    ></span>
                  }
                />
              </CreateLink>
            </div>
          ))}
        </VertCarousel>
      </div>
    );
  }

  return '';
};

const Hero = ({ position=1, ...props }) => {
  const { eventGa } = useGA()
  const { callApi } = usePipeContext();
  const userInfos = useSelector(({ oidc: data }) => data);
  const [ heroName, setHeroName ] = useState(null)

  const bannersItems = useSelector(({ Banners: data }) => data);

  useEffect(() => {
    if (userInfos?.isLoaded && !bannersItems?.requestMake) {
      const {
        user: { profile },
      } = userInfos;
      callApi({
        symbol: `FETCH_BANNERS`,
        alias: null,
        payload: {
          campaign_id: profile['custom:campaign_id'],
          catalog_id: profile['custom:catalog_id']
        },
      });
    }
  }, [bannersItems?.requestMake]);

  const getBanners = () => {
    const { isLoading, requestMake, banners } = bannersItems;
    if (!isLoading && requestMake && banners.length > 0) {
      return banners.sort((a, b) => a.position - b.position);
    }
    return [];
  };

  useEffect(() => {
      const Caller = new CallMethodAusterity()
      if (bannersItems.banners.length && heroName) {
          Caller.callMethod(heroName, () => {
              eventGa("view_promotion", {
                  items: bannersItems.banners.map(({ title, name, id }) => ({
                      creative_name: name,
                      creative_slot: `banner_${ id }`,
                      promotion_name: title,
                  }))
              })
          })
      }
  }, [ bannersItems, heroName ])

    useEffect(() => {
        if (!heroName) {
            setHeroName(generateUUID())
        }
    }, [ heroName ])

  return (
    <PipeProvider>
      <div className="hero margin-bottom-regular">
        <MediaQuery maxWidth={576}>
          <MobileBanners banners={getBanners()} />
        </MediaQuery>
        <MediaQuery minWidth={577} maxWidth={1024}>
          <TabletBanners banners={getBanners()} />
        </MediaQuery>
        <MediaQuery minWidth={1025}>
          <DesktopBanners banners={getBanners()} />
        </MediaQuery>
      </div>
    </PipeProvider>
  );
};
Hero.propTypes = {
  position: PropTypes.number,
};


export default Hero;
